import { ChangeDetectionStrategy, Component, computed, HostListener, inject } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { NbButtonModule, NbIconModule } from "@nebular/theme";
import { Observable, of } from "rxjs";
import { catchError, map, startWith, switchMap } from "rxjs/operators";
import { HelpArticleComponent } from "../help-article/help-article.component";
import { HelpPanelService, HelpTarget } from "../help-panel.service";
import { HelpSectionRef } from "../help-topics";
import { ManualLink, ManualPage, PUBLIC_MANUAL_BASE } from "../manual-html";
import { ManualService } from "../manual.service";

type PanelView = { status: "loading" } | { status: "error" } | { status: "ready"; page: ManualPage };

/**
 * The in-app help panel (work item 830): the current screen's section of the user manual, with the
 * related sections listed underneath as cross-references. Links inside the help open in the panel,
 * so the reader keeps their place in the backoffice.
 */
@Component({
  selector: "ngx-help-panel",
  standalone: true,
  imports: [NbButtonModule, NbIconModule, HelpArticleComponent],
  templateUrl: "./help-panel.component.html",
  styleUrl: "./help-panel.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpPanelComponent {
  readonly help = inject(HelpPanelService);
  private readonly manual = inject(ManualService);
  private readonly router = inject(Router);

  readonly view = toSignal(
    toObservable(this.help.target).pipe(switchMap((target) => this.load(target))),
    { initialValue: { status: "loading" } as PanelView },
  );

  readonly page = computed(() => {
    const view = this.view();
    return view.status === "ready" ? view.page : null;
  });

  /** The same place in the full manual, which opens in a new tab with its search and contents. */
  readonly manualUrl = computed(() => {
    const target = this.help.target();
    return `${PUBLIC_MANUAL_BASE}${target.page}${target.section ? "#" + target.section : ""}`;
  });

  readonly contentsUrl = `${PUBLIC_MANUAL_BASE}index.html`;

  isShowing(ref: HelpSectionRef): boolean {
    const target = this.help.target();
    return target.page === ref.page && target.section === ref.section;
  }

  follow(link: ManualLink): void {
    switch (link.kind) {
      case "anchor":
        this.help.show(this.help.target().page, link.section);
        break;
      case "public":
        this.help.show(link.page, link.section);
        break;
      case "technical":
        // Only technical pages link here; the Loans API decides whether the reader may open them.
        this.router.navigateByUrl(link.url);
        break;
    }
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.help.isOpen()) this.help.close();
  }

  private load(target: HelpTarget): Observable<PanelView> {
    return this.manual.publicPage(target.page).pipe(
      map((page): PanelView => ({ status: "ready", page })),
      startWith<PanelView>({ status: "loading" }),
      catchError(() => of<PanelView>({ status: "error" })),
    );
  }
}
